import { createInternalHref } from "clariah/utils/createInternalHref";
import { locales } from "shared/utils/locales";
import { EN, NL } from "shared/utils/constants";
import { RELATED_POSTS_AMOUNT } from "clariah/utils/constants";
import { getYesterdayISOString } from "shared/utils/formatDate";
import { getClient, previewClientConfig } from "shared/utils/getClient";

import { definePreview } from "next-sanity/preview";

export const usePreview = definePreview(previewClientConfig);

export async function fetchQuery({ query, params, preview }) {
  const client = getClient(preview);
  const result = await client.fetch(query, params);
  return result;
}

export async function fetchPathsForTypes({
  locales,
  types,
  scopeClariah = false,
}) {
  const client = getClient();
  const query = `
  *[_type in $types${scopeClariah === true ? ` && scope.clariah` : ""}]{
    slug.en != null => {
      "en": slug.en.current,
      "nl": slug.nl.current,
    },
    slug.current != null => {
      "slug": slug.current,
    }
  }`;
  const paths = await client.fetch(query, { types });
  return paths
    .map(({ nl, en, slug }) => [
      { params: { slug: nl || slug }, locale: "nl" },
      { params: { slug: en || slug }, locale: "en" },
    ])
    .flat();
}

export async function fetchSearch({ locale = NL, query }) {
  const types = `["clariah.event", "clariah.landing", "clariah.news", "clariah.newsEventsOverview", "clariah.personOverview", "clariah.projectOverview", "clariah.page", "person", "project", "clariah.blog"]`;
  const fields = [
    `title.${locale}`,
    `intro.${locale}`,
    `body.${locale}[].children[].text`,
    `name`,
  ];
  const fieldsString = `[${fields.join(",")}]`;

  const groq = `*[_type in ${types} && ${fieldsString} match "*${query}*" && (!defined(scope) || scope.clariah)] { _type, type, title, intro, slug, name }`;
  const result = await fetchQuery({ query: groq });

  return localize(result, locale);
}

/*
  The localize function localizes all content. It loops over arrays and objects to check if a locale is present
  as a key on an object. It returns the value in its original state with all fields that contain the locale key
  replaced with the value of the provided locale. Note: This assumes there are no fields next to the locale
  key on objects, if there are, those fields are lost.

  const input = {
    title: { nl: 'Dutch', en: 'English', },
    losesFields: { nl: 'Dutch', en: 'English', lostField1: 2, lostField2: 4 }
  }

  const output = localize(input, 'nl') // { title: 'Dutch', losesFields: 'Dutch' }
*/
export function localize(value, locale) {
  if (Array.isArray(value)) {
    return value.map((v) => localize(v, locale));
  }

  if (typeof value === "object" && value !== null) {
    const hasLocaleKey =
      locales.map(({ name }) => value[name]).filter(Boolean).length > 0;

    if (hasLocaleKey) {
      return localize(value[locale], locale);
    }

    if (Object.entries(value).length === 0) {
      return null;
    }

    return Object.entries(value).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: localize(value, locale) }),
      {}
    );
  }

  return value;
}

export async function fetchRelatedPosts({ locale, post }) {
  const { related = [], _type, _id } = post;
  const amount = related
    ? RELATED_POSTS_AMOUNT - related.length
    : RELATED_POSTS_AMOUNT;

  const relatedPostsToFill = await fetchDataForType({
    fields: `{
      heroImage {
        ...,
        "url": image.asset->url
      },
      _type,
      date,
      endDate,
      intro,
      publishedAt,
      slug,
      title,
      scope
    }`,
    locale,
    limit: `[0...${amount}]`,
    order: "isFeatured desc, date desc, publishedAt desc",
    preview: false,
    query: `&& _id != "${_id}" && (date >= "${getYesterdayISOString()}" || endDate >= "${getYesterdayISOString()}")`,
    type: _type,
  });

  return [...(related ?? []), ...relatedPostsToFill];
}
